import { zodResolver } from '@hookform/resolvers/zod';
import { type CreateAndUploadMemoReq, Memo } from '@kalos/kalos-rpc';
import {
  FileUploader,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  toast,
} from '@kalos/ui';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateFullMemoMutation } from '../../../../../hooks/react-query/useMemoQuery';
import {
  IMAGE_UPLOAD_ACCEPT,
  imageFileToStagedFileObject,
  isValidFileType,
  MEMO_STAGING_PREPARE_CONCURRENCY,
  runWithConcurrency,
  VALID_IMAGE_TYPES,
} from '../../../../../tools/fileUploadUtils';
import {
  convertHeicToJpeg,
  easternTimestamp,
  getEnhancedMimeType,
} from '../../../../../tools/helpers';

const photoUploadSchema = z.object({
  files: z.array(z.instanceof(File)).min(1, 'At least one photo is required'),
});

type PhotoUploadSchemaType = z.infer<typeof photoUploadSchema>;

interface Props {
  onSuccess: () => void;
  loggedUserId: number;
  jobId: number;
  onCancel?: () => void;
  onPhotosChange?: (files: File[]) => void;
  onSubmit?: (files: File[]) => Promise<void>;
  isUploading?: boolean;
  onConversionStateChange?: (isConverting: boolean) => void;
}

export interface PhotoUploadFormRef {
  clearFiles: () => void;
}

export const PhotoUploadForm = forwardRef<PhotoUploadFormRef, Props>(
  (
    {
      onSuccess,
      loggedUserId,
      jobId,
      onCancel,
      onPhotosChange,
      onSubmit,
      isUploading = false,
      onConversionStateChange,
    },
    ref,
  ) => {
    const [resetKey, setResetKey] = useState(0);
    const [convertingFiles, setConvertingFiles] = useState<
      { fileName: string; originalName: string }[]
    >([]);

    const form = useForm<PhotoUploadSchemaType>({
      resolver: zodResolver(photoUploadSchema),
      mode: 'onChange',
      defaultValues: {
        files: [],
      },
    });

    const { mutateAsync: createMemo } = useCreateFullMemoMutation();

    const resetForm = useCallback(() => {
      setResetKey((prev) => prev + 1);
      setConvertingFiles([]);
      form.reset({
        files: [],
      });
      onPhotosChange?.([]);
      onConversionStateChange?.(false);
    }, [form, onPhotosChange, onConversionStateChange]);

    const clearAllFiles = useCallback(() => {
      form.setValue('files', []);
      setConvertingFiles([]);
      onPhotosChange?.([]);
      onConversionStateChange?.(false);
    }, [form, onPhotosChange, onConversionStateChange]);

    useImperativeHandle(
      ref,
      () => ({
        clearFiles: clearAllFiles,
      }),
      [clearAllFiles],
    );

    const handleSubmit: SubmitHandler<PhotoUploadSchemaType> = async (data) => {
      if (onSubmit) {
        await onSubmit(data.files);
        return;
      }

      try {
        // Validate all files using shared utility (image types only)
        const validationPromises = data.files.map(async (file) => {
          const isValid = await isValidFileType(file, VALID_IMAGE_TYPES);
          return { file, isValid };
        });

        const validationResults = await Promise.all(validationPromises);
        const validFiles = validationResults
          .filter((result) => result.isValid)
          .map((result) => result.file);
        const invalidFiles = validationResults
          .filter((result) => !result.isValid)
          .map((result) => result.file);

        if (validFiles.length === 0) {
          toast({
            title: 'Upload Failed',
            description:
              'Invalid File Type. Please upload valid image files (JPEG, PNG, WebP, HEIC) or PDF files.',
            variant: 'destructive',
          });
          return;
        }

        if (invalidFiles.length > 0) {
          toast({
            title: 'Some files skipped',
            description: `${invalidFiles.length} file(s) were skipped due to invalid file type. Only valid image files will be uploaded.`,
            variant: 'destructive',
          });
        }

        // Downscale + re-encode each image to keep the inline CreateFullMemo payload
        // below proxy/server size limits (avoids 503 on multi-image uploads).
        const imageFileObjects = await runWithConcurrency(
          validFiles,
          MEMO_STAGING_PREPARE_CONCURRENCY,
          (f) => imageFileToStagedFileObject(f),
        );

        const request: CreateAndUploadMemoReq = {
          memo: Memo.create({
            description: `Photo upload - ${validFiles.length} photo(s) added`,
            createdBy: loggedUserId,
            userId: loggedUserId,
            isActive: true,
            statusId: 1,
            eventId: jobId,
            createdDate: easternTimestamp(),
            classificationId: 1,
            // Bookkeeping, not visit documentation. An external memo on a
            // ServiceChannel job is transmitted to the subscriber's work order.
            isInternal: true,
          }),
          files: {
            files: imageFileObjects,
          },
        };

        await createMemo(request);

        toast({
          title: 'Photos Uploaded',
          description: `Successfully uploaded ${validFiles.length} photo(s) to job notes.`,
          variant: 'success',
        });

        resetForm();
        onSuccess();
      } catch (error) {
        console.error('Error uploading photos:', error);
        toast({
          title: 'Upload Failed',
          description: 'An error occurred while uploading photos.',
          variant: 'destructive',
        });
      }
    };

    const selectedFiles = form.watch('files');
    const isConverting = convertingFiles.length > 0;

    useEffect(() => {
      onPhotosChange?.(selectedFiles);
    }, [selectedFiles, onPhotosChange]);

    const processFiles = useCallback(
      async (files: File[]) => {
        const processedFiles: File[] = [];
        const filesToConvert: { fileName: string; originalName: string }[] = [];

        // First pass: identify HEIC files that need conversion
        for (const file of files) {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const fileData = new Uint8Array(arrayBuffer);
            const enhancedMimeType = getEnhancedMimeType(file.name, fileData);
            const isHeic = enhancedMimeType === 'image/heic' || enhancedMimeType === 'image/heif';

            if (isHeic) {
              const baseName = file.name.replace(/\.[^/.]+$/, '');
              const newFileName = `${baseName}.jpg`;
              filesToConvert.push({
                fileName: newFileName,
                originalName: file.name,
              });
            }
          } catch (error) {
            console.error('Error checking file type:', error);
          }
        }

        if (filesToConvert.length > 0) {
          setConvertingFiles(filesToConvert);
          onConversionStateChange?.(true);
        }

        // Process all files
        for (const file of files) {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const fileData = new Uint8Array(arrayBuffer);
            const enhancedMimeType = getEnhancedMimeType(file.name, fileData);
            const isHeic = enhancedMimeType === 'image/heic' || enhancedMimeType === 'image/heif';

            if (isHeic) {
              try {
                const convertedBlob = await convertHeicToJpeg(
                  fileData,
                  file.name,
                  enhancedMimeType,
                );
                const baseName = file.name.replace(/\.[^/.]+$/, '');
                const newFileName = `${baseName}.jpg`;

                const convertedFile = new File([convertedBlob], newFileName, {
                  type: 'image/jpeg',
                  lastModified: file.lastModified,
                });
                processedFiles.push(convertedFile);
              } catch (conversionError) {
                console.error('Error converting HEIC file:', conversionError);
                processedFiles.push(file);
              }
            } else {
              processedFiles.push(file);
            }
          } catch (error) {
            console.error('Error processing file:', error);
            processedFiles.push(file);
          }
        }

        setConvertingFiles([]);
        onConversionStateChange?.(false);

        return processedFiles;
      },
      [onConversionStateChange],
    );

    return (
      <div className="space-y-6">
        <FormContext {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Take New Photos</h3>

              <FormField
                control={form.control}
                name="files"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FileUploader
                        key={`photo-uploader-${resetKey}`}
                        value={field.value}
                        onValueChange={async (files) => {
                          const actualFiles =
                            typeof files === 'function' ? files(field.value) : files;

                          if (actualFiles.length < field.value.length) {
                            field.onChange(actualFiles);
                          } else {
                            const processedFiles = await processFiles(actualFiles);
                            field.onChange(processedFiles);
                          }
                        }}
                        maxFiles={20}
                        multiple
                        useButtons
                        disabled={isUploading}
                        buttonLabels={{
                          camera: 'Take Photo',
                          browse: 'Browse Files',
                        }}
                        accept={IMAGE_UPLOAD_ACCEPT}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isConverting && (
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <span className="text-sm font-medium">
                    {`Converting ${convertingFiles.length} HEIC file${convertingFiles.length !== 1 ? 's' : ''}...`}
                  </span>
                </div>
              )}
            </div>
          </form>
        </FormContext>
      </div>
    );
  },
);

PhotoUploadForm.displayName = 'PhotoUploadForm';
